import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Play, Upload, Save, Loader2, AlertCircle, RotateCcw, X } from 'lucide-react';
import Button from '../common/Button';

interface AudioRecorderProps {
  onSave: (audioBlob: Blob) => Promise<void>;
  maxDuration?: number;
  disabled?: boolean;
}

export default function AudioRecorder({
  onSave,
  maxDuration = 60,
  disabled = false
}: AudioRecorderProps) {
  const [mode, setMode] = useState<'select' | 'record' | 'upload'>('select');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [audioDuration, setAudioDuration] = useState<number>(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  const validateAudioFile = async (file: File): Promise<boolean> => {
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return false;
    }

    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/mp4'];
    if (!allowedTypes.includes(file.type)) {
      setError('File type must be MP3, WAV, or M4A');
      return false;
    }

    return new Promise((resolve) => {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);

      audio.onloadedmetadata = () => {
        if (audio.duration > maxDuration) {
          setError(`Audio must be ${maxDuration} seconds or less`);
          URL.revokeObjectURL(audio.src);
          resolve(false);
        } else {
          setAudioDuration(audio.duration);
          URL.revokeObjectURL(audio.src);
          resolve(true);
        }
      };

      audio.onerror = () => {
        setError('Invalid audio file');
        URL.revokeObjectURL(audio.src);
        resolve(false);
      };
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const isValid = await validateAudioFile(file);

    if (isValid) {
      setAudioBlob(file);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      setFileName(file.name);
      setMode('upload');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    if (disabled || isSaving) return;

    try {
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          setError('Recording failed. No audio data captured.');
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        if (audioBlob.size === 0) {
          setError('Recording failed. Audio file is empty.');
          return;
        }

        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setAudioDuration(recordingTime);

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      setMode('record');

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);

    } catch (err) {
      const error = err as Error;
      console.error('Error accessing microphone:', err);

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Failed to access microphone. Please check your browser permissions.');
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const playRecording = () => {
    if (!audioUrl || isPlaying) return;

    try {
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;

      audio.onended = () => {
        setIsPlaying(false);
        audioPlayerRef.current = null;
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setError('Failed to play recording.');
        audioPlayerRef.current = null;
      };

      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Failed to play recording.');
        setIsPlaying(false);
      });

      setIsPlaying(true);
    } catch (err) {
      console.error('Error creating audio player:', err);
      setError('Failed to play recording.');
    }
  };

  const handleSave = async () => {
    if (!audioBlob || isSaving) return;

    if (audioBlob.size === 0) {
      setError('Cannot save empty recording.');
      return;
    }

    if (audioBlob.size > 5 * 1024 * 1024) {
      setError('Recording is too large (max 5MB).');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(audioBlob);

      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      setMode('select');
      setFileName('');
      setAudioDuration(0);

      cleanup();
    } catch (err) {
      console.error('Error saving recording:', err);
      const errorMessage = (err as Error).message || 'Failed to save recording.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (isSaving || isRecording) return;

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }

    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
    setError(null);
    setMode('select');
    setFileName('');
    setAudioDuration(0);

    cleanup();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (mode === 'select') {
    return (
      <div className="space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={startRecording}
            icon={Mic}
            disabled={disabled}
          >
            Record Audio
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            icon={Upload}
            disabled={disabled}
          >
            Upload File
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.m4a"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <p className="text-xs text-gray-500">
          Record or upload an audio message (max 60 seconds, 5MB). For best compatibility, upload MP3 files.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {fileName && (
        <div className="text-sm text-gray-700 bg-gray-50 rounded p-2">
          <span className="font-medium">File:</span> {fileName}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {isRecording && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={stopRecording}
              icon={Square}
              className="border-red-500 text-red-600 hover:bg-red-50"
            >
              Stop Recording
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-gray-700">
                {formatTime(recordingTime)} / {formatTime(maxDuration)}
              </span>
            </div>
          </>
        )}

        {audioBlob && !isRecording && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={playRecording}
              disabled={isPlaying || isSaving}
              icon={Play}
            >
              {isPlaying ? 'Playing...' : 'Play'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              icon={isSaving ? Loader2 : Save}
              className={isSaving ? 'opacity-75' : ''}
            >
              {isSaving ? 'Uploading...' : 'Save Audio'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isSaving}
              icon={RotateCcw}
            >
              Reset
            </Button>
            <span className="text-sm text-gray-600">
              Duration: {formatTime(audioDuration)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
